<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getLabel } from '@/utils/data-display-utils';

import LocaleSelect from '../LocaleSelect.vue';
import BaseButton from '../base/BaseButton.vue';

import { useCommunityStore } from '@/store/communityStore';
import { useUserStore } from '@/store/userStore';
import { getTranslation } from '@/utils/data-display-utils';
import { authService } from '@/services/auth.service';
import { paths } from '@/utils/paths';
import { Locales } from '@/store/language.interface';

// EMITS
const emit = defineEmits<{
  (e: 'openLoginDialog'): void;
}>();

// STORE
const communityStore = useCommunityStore();
const userStore = useUserStore();

const props = defineProps<{
  languageEnabled?: Locales[];
}>();

// CONST
const router = useRouter();

// REFS
const isMenuOpen = ref<boolean>(false);

// COMPUTED
const displayedIcon = computed(() => {
  return isMenuOpen.value ? 'close' : 'menu';
});

// FUNCTIONS
function logout() {
  router.push(paths.root);
  authService.logout();
  isMenuOpen.value = false;
}
</script>

<template>
  <q-btn unelevated>
    <q-icon :name="displayedIcon" />
    <q-menu class="mobile-menu" v-model="isMenuOpen" @before-show="isMenuOpen = true" @before-hide="isMenuOpen = false">
      <div class="mobile-menu">
        <div class="mobile-menu__title">
          <div>{{ getTranslation(communityStore.community, 'title', props.languageEnabled) }}</div>
        </div>
        <q-separator />
        <div class="mobile-menu__lang">
          <div class="lang">{{ getLabel('language', props.languageEnabled) }}</div>
          <LocaleSelect class="" v-if="props.languageEnabled.length > 1" :language-enabled="props.languageEnabled" />
        </div>
        <q-separator />

        <div class="mobile-menu__about">
          <div class="title">{{ getLabel('about-us', props.languageEnabled) }}</div>
          <div class="about">
            {{
              getLabel('about.cocoriko-platform', props.languageEnabled, {
                named: {
                  client: getTranslation(communityStore.community, 'title', props.languageEnabled),
                },
              })
            }}
          </div>
        </div>
        <div class="mobile-menu__logout">
          <BaseButton
            v-if="userStore.isUserLogged"
            :text="getLabel('account.logout', props.languageEnabled)"
            color="secondary"
            left-icon="logout"
            full-width
            class="logout-btn"
            @click="logout"
          />
          <BaseButton
            v-else
            :text="getLabel('account.login', props.languageEnabled)"
            color="secondary"
            left-icon="login"
            full-width
            class="logout-btn"
            @click="emit('openLoginDialog')"
          />
        </div>
      </div>
    </q-menu>
  </q-btn>
</template>

<style lang="scss">
.mobile-menu {
  display: flex;
  flex-direction: column;
  font-size: $body-text;
  height: 100%;

  &__title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    font-size: $medium-title;
    font-weight: bold;
  }

  &__lang {
    display: flex;
    flex-direction: column;

    .lang {
      font-weight: bold;
    }
  }

  &__about {
    &-title {
      font-weight: bold;
      margin: $space-sm 0;
    }
  }

  &__logout {
    display: flex;
    width: inherit;
    margin-top: auto;

    .logout-btn {
      .q-icon {
        margin-right: $space-sm;
      }
    }
  }

  &.q-position-engine {
    right: 0 !important;
    left: auto !important;
    top: 56px !important;
    height: 100vh;
    width: 85vw;
    padding: 32px;
  }

  .q-separator {
    margin: $space-sm 0;
  }

  &.q-menu {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }
}
</style>
